import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../hooks/AppContext';
import { api } from '../utils/api';
import { fmt, statusBadge, getRentMonth, getPeriodYears, MONTHS } from '../utils/helpers';
import ErrorModal from './ErrorModal';
import * as XLSX from 'xlsx';

// Sentinel option value — picking it opens the "create master" dialog
// instead of selecting a value.
const NEW = '__NEW__';

const MASTER_LABEL = {
  branch: 'CBS Branch',
  acode: 'Acode',
  bank: 'Bank',
  vendor: 'Vendor',
};

export default function DataManage() {
  const { activeMonth, isSimulating } = useApp();
  const [data, setData] = useState(null);
  const [masters, setMasters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchF, setBranchF] = useState('');
  const [search, setSearch] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [diffModal, setDiffModal] = useState(null);
  const [masterModal, setMasterModal] = useState(null);
  const [errModal, setErrModal] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getGodowns(activeMonth)); }
    catch (err) { console.error(err); }
    setLoading(false);
  }, [activeMonth]);

  const loadMasters = useCallback(async () => {
    try { setMasters(await api.getMasters()); }
    catch (err) { console.error(err); setMasters({ branches: [], acodes: [], banks: [], vendors: [] }); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadMasters(); }, [loadMasters]);

  // The upload summary carries the row-by-row errors, so it waits for the
  // user rather than disappearing on a timer.
  useEffect(() => {
    if (!uploadResult) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setUploadResult(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [uploadResult]);

  if (loading || !data || !masters) return <div className="wrap"><div className="loading">Loading...</div></div>;

  const rows = data.data;
  const branches = [...new Set(rows.map(r => r.cbsbrcd))].sort();

  let filtered = rows;
  if (branchF) filtered = filtered.filter(r => r.cbsbrcd === branchF);
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(r =>
      r.godown_name?.toLowerCase().includes(s) || r.vendor_name?.toLowerCase().includes(s) ||
      r.pan_no?.toLowerCase().includes(s) || String(r.godown_id).includes(s)
    );
  }

  const fail = (messages, title) => setErrModal({ messages: Array.isArray(messages) ? messages : [messages], title });

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const wb = XLSX.read(ev.target.result, { type: 'array' });
      const xrows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
      try {
        const result = await api.uploadGodowns(xrows);
        setUploadResult(result);
        loadData();
        loadMasters();
      } catch (err) { fail(err.message, 'Upload failed'); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleExport = () => {
    const d = rows.map(r => ({
      godown_id: r.godown_id, cbsbrcd: r.cbsbrcd, acode: r.acode,
      godown_name: r.godown_name, gm_name: r.gm_name,
      vendor_id: r.vendor_id, vendor_name: r.vendor_name, pan_no: r.pan_no,
      account_no: r.account_no, ifsc: r.ifsc, bank_name: r.bank_name,
      fa_subledger: r.fa_subledger, rent_start_period: r.rent_start_period,
      monthly_rent: r.monthly_rent, sgst: r.sgst, cgst: r.cgst, igst: r.igst,
      utgst: r.utgst, tds: r.tds, mded: r.mded, net_amt: r.net_amt,
      pay_mode: r.pay_mode, pay_nature: r.pay_nature,
      security_deposit: r.security_deposit, other_advance: r.other_advance, remarks: r.remarks,
    }));
    const ws = XLSX.utils.json_to_sheet(d);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Godowns');
    XLSX.writeFile(wb, 'Godown_Data_Export.xlsx');
  };

  const handleEdit = (r) => {
    setEditModal({ ...r, _original: { ...r } });
  };

  // Same rules the upload path enforces server-side (api/utils/validation.js),
  // so a new record fails here with a readable message instead of a DB error.
  const PAN_RE = /^[A-Z]{3}[ABCFHPT][A-Z][0-9]{4}[A-Z]$/;
  const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

  const handleCreate = async () => {
    const d = editModal;
    const errs = [];
    [['cbsbrcd','CBS Branch'],['acode','Acode'],['godown_name','Godown Name'],
     ['vendor_name','Vendor Name'],['pan_no','PAN'],['account_no','Account No'],
     ['ifsc','IFSC'],['bank_name','Bank'],['fa_subledger','FA Subledger Mapping'],
     ['rent_start_period','Rent Starting Period']]
      .forEach(([f, label]) => { if (!String(d[f] ?? '').trim()) errs.push(`${label} is required`); });
    if (d.pan_no && !PAN_RE.test(d.pan_no)) errs.push('PAN must be ABCDE1234F (4th char: A/B/C/F/H/P/T)');
    if (d.ifsc && !IFSC_RE.test(d.ifsc)) errs.push('IFSC must be ABCD0XXXXXX');
    if (!(Number(d.monthly_rent) > 0)) errs.push('Monthly Rent must be positive');
    if (Number(d.tds || 0) > Number(d.monthly_rent || 0)) errs.push('TDS cannot exceed Monthly Rent');
    if (errs.length) { fail(errs, 'This record cannot be added yet'); return; }

    // IDs (godown/vendor/bank) are left blank — the server generates them per branch.
    try {
      await api.createGodown({
        cbsbrcd: d.cbsbrcd.trim(), acode: d.acode.trim(),
        godown_name: d.godown_name.trim(), gm_name: String(d.gm_name ?? '').trim(),
        vendor_name: d.vendor_name.trim(), pan_no: d.pan_no.trim(),
        account_no: d.account_no.trim(), ifsc: d.ifsc.trim(), bank_name: d.bank_name.trim(),
        fa_subledger: d.fa_subledger.trim(), rent_start_period: d.rent_start_period.trim(),
        monthly_rent: Number(d.monthly_rent), sgst: Number(d.sgst || 0), cgst: Number(d.cgst || 0),
        igst: Number(d.igst || 0), utgst: Number(d.utgst || 0), tds: Number(d.tds || 0),
        mded: Number(d.mded || 0), pay_mode: d.pay_mode || 'On Account',
        pay_nature: d.pay_nature || 'Cur',
        security_deposit: Number(d.security_deposit || 0),
        other_advance: Number(d.other_advance || 0),
        remarks: String(d.remarks ?? '').trim(),
      });
      setEditModal(null);
      loadData();
      loadMasters();
    } catch (err) { fail(err.message, 'Could not add record'); }
  };

  const handleSave = async () => {
    // New records have no _original to diff against — they go straight to the API.
    if (editModal._isNew) return handleCreate();
    const d = editModal;
    const orig = d._original || {};
    const changes = {};
    const fields = ['monthly_rent','sgst','cgst','igst','utgst','tds','mded','pay_mode','pay_nature',
      'vendor_name','pan_no','account_no','ifsc','bank_name','security_deposit','other_advance','remarks','godown_name','gm_name',
      'fa_subledger','rent_start_period','cbsbrcd','acode'];
    const labels = {monthly_rent:'Monthly Rent',sgst:'SGST',cgst:'CGST',igst:'IGST',utgst:'UTGST',tds:'TDS',
      mded:'Manual Deduction',pay_mode:'Pay Mode',pay_nature:'Rent Type',vendor_name:'Vendor Name',
      pan_no:'PAN',account_no:'Account No',ifsc:'IFSC',bank_name:'Bank',security_deposit:'Security Deposit',
      other_advance:'Other Advance',remarks:'Remarks',godown_name:'Godown Name',gm_name:'GM Name',
      fa_subledger:'FA Subledger Mapping',rent_start_period:'Rent Starting Period',
      cbsbrcd:'CBS Branch',acode:'Acode'};

    if (!String(d.acode ?? '').trim()) { fail('Acode is required', 'This record cannot be saved'); return; }

    fields.forEach(f => {
      if (String(d[f] ?? '') !== String(orig[f] ?? '')) {
        changes[f] = { from: orig[f], to: d[f], label: labels[f] || f };
      }
    });

    if (Object.keys(changes).length === 0) { fail('Nothing was changed on this record.', 'No changes to save'); return; }
    setDiffModal({ changes, godown: d });
  };

  const confirmSave = async () => {
    const d = diffModal.godown;
    const changeValues = {};
    Object.entries(diffModal.changes).forEach(([f, v]) => { changeValues[f] = v.to; });

    try {
      await api.editGodown(d.godown_id, d.vendor_id, activeMonth, changeValues);
      setDiffModal(null);
      setEditModal(null);
      loadData();
      loadMasters();
    } catch (err) { fail(err.message, 'Could not save changes'); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(
      `Delete godown ${r.godown_name} (#${r.godown_id}) permanently?\n\n` +
      'Its month overrides and confirmations are removed with it. This cannot be undone.'
    )) return;
    try { await api.deleteGodown(r.godown_id, r.vendor_id); loadData(); }
    catch (err) { fail(err.message, 'Could not delete record'); }
  };

  // ── Master dropdown plumbing ──────────────────────────────────────────
  // A record loaded from the database may carry a value that is not in the
  // master (an old upload, say). Keeping it in the list means editing an
  // unrelated field cannot silently blank it.
  const withCurrent = (opts, current) =>
    (current && !opts.includes(current)) ? [current, ...opts] : opts;

  const openMaster = (type, seed = {}) => setMasterModal({ type, ...seed });

  const submitMaster = async () => {
    const m = masterModal;
    try {
      const { entry } = await api.createMaster(m.type, m);
      const fresh = await api.getMasters();
      setMasters(fresh);
      // Select what was just created, so the dropdown lands on it.
      if (m.type === 'branch') setEditModal(p => ({ ...p, cbsbrcd: entry.cbsbrcd, acode: '' }));
      if (m.type === 'acode') setEditModal(p => ({ ...p, acode: entry.acode }));
      if (m.type === 'bank') setEditModal(p => ({ ...p, bank_name: entry.bank_name }));
      if (m.type === 'vendor') setEditModal(p => ({ ...p, vendor_name: entry.vendor_name, pan_no: entry.pan_no }));
      setMasterModal(null);
    } catch (err) {
      fail(err.message, `Could not create ${MASTER_LABEL[m.type]}`);
    }
  };

  // Rent Starting Period is stored as "MMM-YYYY"; the two pickers below are
  // just a safe way to compose that string.
  const periodParts = String(editModal?.rent_start_period || '').split('-');
  const periodMon = MONTHS.includes(periodParts[0]) ? periodParts[0] : '';
  const periodYr = /^\d{4}$/.test(periodParts[1] || '') ? periodParts[1] : '';
  const setPeriod = (mon, yr) =>
    setEditModal(p => ({ ...p, rent_start_period: mon && yr ? `${mon}-${yr}` : '' }));

  return (
    <div className="wrap">
      <div className="page-header">
        <div>
          <h2>Manage Godown Data</h2>
          <p className="sub">{rows.length} records · Showing status for:
            <strong style={{color: isSimulating ? 'var(--acc)' : 'var(--pri)'}}> {data.month}</strong>
            {isSimulating ? ' (simulated)' : ''}
          </p>
        </div>
        <div className="btn-group">
          <button className="btn btn-o btn-sm" onClick={handleExport}>📥 Export</button>
          <label className="btn btn-a btn-sm" style={{cursor:'pointer'}}>
            📤 Upload <input type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} hidden />
          </label>
          <button className="btn btn-p btn-sm" onClick={() => setEditModal({_isNew: true, monthly_rent: 0, sgst: 0, cgst: 0, igst: 0, utgst: 0, tds: 0, mded: 0})}>
            + Add Record
          </button>
        </div>
      </div>

      {/* Upload result — dismissed by ✕ or ESC, never on a timer */}
      {uploadResult && (
        <div className={`upload-result ${uploadResult.errors?.length ? 'has-errors' : ''}`}>
          <button className="modal-x" onClick={() => setUploadResult(null)} aria-label="Dismiss">✕</button>
          <div className="upload-summary">
            {uploadResult.errors?.length ? '⚠️ Upload with issues' : '✅ Upload successful'} —
            Total: <strong>{uploadResult.total}</strong> · Added: <strong className="text-green">{uploadResult.added}</strong> ·
            Updated: <strong style={{color:'var(--amb)'}}>{uploadResult.updated}</strong> ·
            Skipped: <strong className="text-red">{uploadResult.skipped}</strong>
          </div>
          {uploadResult.errors?.length > 0 && (
            <details open><summary className="text-red">🔴 {uploadResult.errors.length} Error(s)</summary>
              <div className="error-list">{uploadResult.errors.map((e,i) => <div key={i}>Row {e.row}: <strong>{e.type}</strong> — {e.msg}</div>)}</div>
            </details>
          )}
          {uploadResult.warnings?.length > 0 && (
            <details><summary style={{color:'var(--amb)'}}>🟡 {uploadResult.warnings.length} Warning(s)</summary>
              <div className="warning-list">{uploadResult.warnings.map((w,i) => <div key={i}>Row {w.row}: <strong>{w.type}</strong> — {w.msg}</div>)}</div>
            </details>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="fbar">
        <select value={branchF} onChange={e => setBranchF(e.target.value)}>
          <option value="">All Branches</option>
          {branches.map(b => <option key={b}>{b}</option>)}
        </select>
        <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Table */}
      <div className="tbl-wrap">
        <table>
          <thead><tr>
            <th>ID</th><th>Branch</th><th>Acode</th><th>Godown</th><th>Vendor</th>
            <th className="text-right">Rent</th><th className="text-right">Net</th>
            <th>Type</th><th>Status ({data.month})</th><th>Bank</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(r => (
              <tr key={`${r.godown_id}_${r.vendor_id}`} className={r.status === 'yes' ? 'row-yes' : r.status === 'expired' ? 'row-expired' : ''}>
                <td className="mono">{r.godown_id}</td>
                <td><strong>{r.cbsbrcd}</strong></td>
                <td>{r.acode}</td>
                <td className="fw-500">{r.godown_name}</td>
                <td>
                  <div>{r.vendor_name}</div>
                  <div className="text-xs text-muted">V:{r.vendor_id} PAN:{r.pan_no}</div>
                </td>
                <td className="text-right">₹{fmt(r.monthly_rent)}</td>
                <td className="text-right fw-600">₹{fmt(r.net_amt)}</td>
                <td>
                  <div className="fw-700" style={{color:'var(--pri)'}}>{getRentMonth(data.month, r.pay_nature)}</div>
                  <span className={r.pay_nature === 'Adv' ? 'type-adv' : 'type-cur'}>
                    {r.pay_nature === 'Adv' ? 'Prepaid' : 'Postpaid'}
                  </span>
                </td>
                <td>{statusBadge(r.status)}</td>
                <td className="text-sm">{r.bank_name}<br/><span className="mono text-xs text-muted">{r.ifsc}</span></td>
                <td>
                  <div className="btn-group-sm">
                    <button className="btn btn-o btn-sm" onClick={() => handleEdit(r)}>Edit</button>
                    <button className="btn btn-d btn-sm" onClick={() => handleDelete(r)}>Del</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editModal && !diffModal && (
        <div className="overlay" onClick={e => e.target.className === 'overlay' && setEditModal(null)}>
          <div className="modal modal-wide">
            <h3>{editModal._isNew ? '➕ Add Record' : `✏️ Edit #${editModal.godown_id}`}</h3>
            <div className="form-grid">
              <div className="field"><label>Godown Name *</label>
                <input value={editModal.godown_name || ''} onChange={e => setEditModal(p => ({...p, godown_name: e.target.value}))} /></div>

              <div className="field"><label>CBS Branch *</label>
                <select value={editModal.cbsbrcd || ''} onChange={e => {
                  if (e.target.value === NEW) { openMaster('branch', { cbsbrcd: '', branch_name: '' }); return; }
                  // Acode belongs to a branch, so changing branch clears it.
                  setEditModal(p => ({...p, cbsbrcd: e.target.value, acode: ''}));
                }}>
                  <option value="">— Select branch —</option>
                  {withCurrent(masters.branches.map(b => b.cbsbrcd), editModal.cbsbrcd).map(b =>
                    <option key={b} value={b}>{b}</option>)}
                  <option value={NEW}>+ Create new branch…</option>
                </select></div>

              <div className="field"><label>Acode *</label>
                <select value={editModal.acode || ''} disabled={!editModal.cbsbrcd} onChange={e => {
                  if (e.target.value === NEW) { openMaster('acode', { cbsbrcd: editModal.cbsbrcd, acode: '', description: '' }); return; }
                  setEditModal(p => ({...p, acode: e.target.value}));
                }}>
                  <option value="">{editModal.cbsbrcd ? '— Select acode —' : '— Pick a branch first —'}</option>
                  {withCurrent(
                    masters.acodes.filter(a => a.cbsbrcd === editModal.cbsbrcd).map(a => a.acode),
                    editModal.acode
                  ).map(a => <option key={a} value={a}>{a}</option>)}
                  {editModal.cbsbrcd && <option value={NEW}>+ Create new acode…</option>}
                </select></div>

              <div className="field"><label>GM Name</label>
                <input value={editModal.gm_name || ''} onChange={e => setEditModal(p => ({...p, gm_name: e.target.value}))} /></div>

              <div className="form-section">Vendor</div>
              <div className="field"><label>Vendor Name *</label>
                <select value={editModal.pan_no || ''} onChange={e => {
                  if (e.target.value === NEW) { openMaster('vendor', { vendor_name: '', pan_no: '' }); return; }
                  const v = masters.vendors.find(x => x.pan_no === e.target.value);
                  setEditModal(p => ({...p, pan_no: e.target.value, vendor_name: v ? v.vendor_name : p.vendor_name}));
                }}>
                  <option value="">— Select vendor —</option>
                  {masters.vendors.map(v =>
                    <option key={v.pan_no} value={v.pan_no}>{v.vendor_name} — {v.pan_no}</option>)}
                  {editModal.pan_no && !masters.vendors.some(v => v.pan_no === editModal.pan_no) && (
                    <option value={editModal.pan_no}>{editModal.vendor_name} — {editModal.pan_no}</option>
                  )}
                  <option value={NEW}>+ Create new vendor…</option>
                </select></div>
              <div className="field"><label>PAN *</label>
                <input value={editModal.pan_no || ''} readOnly className="ro"
                  placeholder="Set by the vendor you pick" /></div>

              <div className="form-section">Banking (held per godown)</div>
              <div className="field"><label>Bank *</label>
                <select value={editModal.bank_name || ''} onChange={e => {
                  if (e.target.value === NEW) { openMaster('bank', { bank_name: '' }); return; }
                  setEditModal(p => ({...p, bank_name: e.target.value}));
                }}>
                  <option value="">— Select bank —</option>
                  {withCurrent(masters.banks.map(b => b.bank_name), editModal.bank_name).map(b =>
                    <option key={b} value={b}>{b}</option>)}
                  <option value={NEW}>+ Create new bank…</option>
                </select></div>
              <div className="field"><label>Account No *</label>
                <input value={editModal.account_no || ''} onChange={e => setEditModal(p => ({...p, account_no: e.target.value}))} /></div>
              <div className="field"><label>IFSC *</label>
                <input value={editModal.ifsc || ''} onChange={e => setEditModal(p => ({...p, ifsc: e.target.value.toUpperCase()}))} placeholder="ABCD0XXXXXX" /></div>
              <div className="field"><label>FA Subledger Mapping *</label>
                <input value={editModal.fa_subledger || ''} onChange={e => setEditModal(p => ({...p, fa_subledger: e.target.value}))} placeholder="e.g. SL-4521" /></div>

              <div className="form-section">Rent & Payment</div>
              <div className="field"><label>Monthly Rent (₹) *</label>
                <input type="number" value={editModal.monthly_rent || ''} onChange={e => setEditModal(p => ({...p, monthly_rent: Number(e.target.value)}))} /></div>
              <div className="field"><label>Pay Mode</label>
                <select value={editModal.pay_mode || 'On Account'} onChange={e => setEditModal(p => ({...p, pay_mode: e.target.value}))}>
                  <option>On Account</option><option>Cash</option><option>Cheque</option><option>NEFT/RTGS</option>
                </select></div>
              <div className="field"><label>Rent Starting Period *</label>
                <div className="split-2">
                  <select value={periodMon} onChange={e => setPeriod(e.target.value, periodYr)}>
                    <option value="">Month</option>
                    {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select value={periodYr} onChange={e => setPeriod(periodMon, e.target.value)}>
                    <option value="">Year</option>
                    {getPeriodYears().map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div></div>
              <div className="field"><label>Rent Type</label>
                <select value={editModal.pay_nature || 'Cur'} onChange={e => setEditModal(p => ({...p, pay_nature: e.target.value}))}>
                  <option value="Cur">Postpaid (Current)</option><option value="Adv">Prepaid (Advance)</option>
                </select></div>

              <div className="form-section">Tax & Deductions</div>
              <div className="field"><label>SGST ₹</label><input type="number" value={editModal.sgst || ''} onChange={e => setEditModal(p => ({...p, sgst: Number(e.target.value)}))} /></div>
              <div className="field"><label>CGST ₹</label><input type="number" value={editModal.cgst || ''} onChange={e => setEditModal(p => ({...p, cgst: Number(e.target.value)}))} /></div>
              <div className="field"><label>IGST ₹</label><input type="number" value={editModal.igst || ''} onChange={e => setEditModal(p => ({...p, igst: Number(e.target.value)}))} /></div>
              <div className="field"><label>UTGST ₹</label><input type="number" value={editModal.utgst || ''} onChange={e => setEditModal(p => ({...p, utgst: Number(e.target.value)}))} /></div>
              <div className="field"><label>TDS ₹</label><input type="number" value={editModal.tds || ''} onChange={e => setEditModal(p => ({...p, tds: Number(e.target.value)}))} /></div>
              <div className="field"><label>Manual Deduction ₹</label><input type="number" value={editModal.mded || ''} onChange={e => setEditModal(p => ({...p, mded: Number(e.target.value)}))} /></div>

              <div className="form-section">Other</div>
              <div className="field"><label>Security Deposit ₹</label><input type="number" value={editModal.security_deposit || ''} onChange={e => setEditModal(p => ({...p, security_deposit: Number(e.target.value)}))} /></div>
              <div className="field"><label>Other Advance ₹</label><input type="number" value={editModal.other_advance || ''} onChange={e => setEditModal(p => ({...p, other_advance: Number(e.target.value)}))} /></div>
              <div className="field field-full"><label>Remarks</label><input value={editModal.remarks || ''} onChange={e => setEditModal(p => ({...p, remarks: e.target.value}))} /></div>
            </div>

            {/* Net amount display */}
            <div className="net-display">
              Rent: ₹{fmt(editModal.monthly_rent || 0)} = Net: <strong className="text-green">
                ₹{fmt((editModal.monthly_rent||0)+(editModal.sgst||0)+(editModal.cgst||0)+(editModal.igst||0)+(editModal.utgst||0)-(editModal.tds||0)-(editModal.mded||0))}
              </strong>
            </div>

            <div className="modal-act">
              <button className="btn btn-o" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-p" onClick={handleSave}>
                {editModal._isNew ? 'Add Record' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create master modal — sits on top of the record form */}
      {masterModal && (
        <div className="overlay" onClick={e => e.target.className === 'overlay' && setMasterModal(null)}>
          <div className="modal" style={{maxWidth: 420}}>
            <h3>➕ New {MASTER_LABEL[masterModal.type]} Master</h3>

            {masterModal.type === 'branch' && (<>
              <div className="field"><label>CBS Branch Code *</label>
                <input autoFocus value={masterModal.cbsbrcd || ''} placeholder="e.g. BNG"
                  onChange={e => setMasterModal(p => ({...p, cbsbrcd: e.target.value.toUpperCase()}))} /></div>
              <div className="field"><label>Branch Name</label>
                <input value={masterModal.branch_name || ''}
                  onChange={e => setMasterModal(p => ({...p, branch_name: e.target.value}))} /></div>
            </>)}

            {masterModal.type === 'acode' && (<>
              <div className="field"><label>CBS Branch</label>
                <input value={masterModal.cbsbrcd || ''} readOnly className="ro" /></div>
              <div className="field"><label>Acode *</label>
                <input autoFocus value={masterModal.acode || ''}
                  onChange={e => setMasterModal(p => ({...p, acode: e.target.value}))} /></div>
              <div className="field"><label>Description</label>
                <input value={masterModal.description || ''}
                  onChange={e => setMasterModal(p => ({...p, description: e.target.value}))} /></div>
            </>)}

            {masterModal.type === 'bank' && (
              <div className="field"><label>Bank Name *</label>
                <input autoFocus value={masterModal.bank_name || ''} placeholder="e.g. State Bank of India"
                  onChange={e => setMasterModal(p => ({...p, bank_name: e.target.value}))} /></div>
            )}

            {masterModal.type === 'vendor' && (<>
              <div className="field"><label>Vendor Name *</label>
                <input autoFocus value={masterModal.vendor_name || ''}
                  onChange={e => setMasterModal(p => ({...p, vendor_name: e.target.value}))} /></div>
              <div className="field"><label>PAN *</label>
                <input value={masterModal.pan_no || ''} placeholder="ABCDE1234F"
                  onChange={e => setMasterModal(p => ({...p, pan_no: e.target.value.toUpperCase()}))} /></div>
            </>)}

            <p className="text-xs text-muted">Added to the master list for every branch to use.</p>
            <div className="modal-act">
              <button className="btn btn-o" onClick={() => setMasterModal(null)}>Cancel</button>
              <button className="btn btn-p" onClick={submitMaster}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Diff confirmation modal */}
      {diffModal && (
        <div className="overlay" onClick={e => e.target.className === 'overlay' && setDiffModal(null)}>
          <div className="modal" style={{maxWidth: 440}}>
            <div style={{textAlign:'center', fontSize: 36}}>📝</div>
            <h3 style={{textAlign:'center'}}>Confirm Changes</h3>
            <p className="text-sm text-center text-muted">
              Godown: <strong>{diffModal.godown.godown_name}</strong> (#{diffModal.godown.godown_id})
              · Applies from: <strong className="text-amber">{activeMonth}</strong> onwards
            </p>
            <table className="diff-table">
              <thead><tr><th>Field</th><th>Current</th><th>New Value</th></tr></thead>
              <tbody>
                {Object.values(diffModal.changes).map(c => (
                  <tr key={c.label}>
                    <td className="fw-600">{c.label}</td>
                    <td className="text-red">{typeof c.from === 'number' ? `₹${fmt(c.from)}` : c.from || '—'}</td>
                    <td className="text-green fw-600">{typeof c.to === 'number' ? `₹${fmt(c.to)}` : c.to || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modal-act-center">
              <button className="btn btn-o" onClick={() => {
                const orig = diffModal.godown._original;
                setDiffModal(null);
                setEditModal({ ...orig, _original: { ...orig } });
              }}>← Cancel</button>
              <button className="btn btn-p" onClick={confirmSave}>✅ Apply Changes</button>
            </div>
          </div>
        </div>
      )}

      {errModal && (
        <ErrorModal title={errModal.title} messages={errModal.messages} onClose={() => setErrModal(null)} />
      )}
    </div>
  );
}
